import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  useForm,
  useWatch,
  Controller,
  type Control,
  type Path,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ParticipantPreferencesSchema } from "@shared/schemas/participant-preferences.schemas";
import type { ParticipantPreferences } from "@shared/schemas/participant-preferences.schemas";
import { Switch } from "@planta-v2/pds-ui/switch";
import { Label } from "@planta-v2/pds-ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@planta-v2/pds-ui/select";
import { Skeleton } from "@planta-v2/pds-ui/skeleton";
import { i18n } from "@frontend/i18n";

type PreferencesFormControlType = Control<ParticipantPreferences>;
type PreferencesFieldPathType = Path<ParticipantPreferences>;
type ContactSharingFieldType = "whatsapp" | "email";

interface PreferencesFormProps {
  readonly initialData: ParticipantPreferences | undefined;
  readonly onFieldChange: (data: ParticipantPreferences) => void;
  readonly isLoading: boolean;
  readonly beforeActions?: React.ReactNode;
}

function useForwardFieldChanges(
  control: PreferencesFormControlType,
  onFieldChange: (data: ParticipantPreferences) => void,
): void {
  const currentValues = useWatch({ control });
  const loadedValuesRef = useRef(currentValues);
  const onFieldChangeRef = useRef(onFieldChange);

  useEffect(() => {
    onFieldChangeRef.current = onFieldChange;
  }, [onFieldChange]);

  useEffect(() => {
    // The mounting values are the ones just loaded, not an edit. The parent's
    // last-saved snapshot syncs after this child effect, so it can't filter them.
    if (currentValues === loadedValuesRef.current) {
      return;
    }
    const parsed = ParticipantPreferencesSchema.safeParse(currentValues);
    if (!parsed.success) {
      return;
    }
    onFieldChangeRef.current(parsed.data);
  }, [currentValues]);
}

interface ContactSharingGuardResult {
  readonly handleGuard: (
    field: ContactSharingFieldType,
    newValue: boolean,
  ) => boolean;
  readonly isFlashing: boolean;
  readonly showHint: boolean;
}

function useContactSharingGuard(
  control: PreferencesFormControlType,
): ContactSharingGuardResult {
  const currentValues = useWatch({ control });
  const contactSharingWhatsApp =
    currentValues.contactSharing?.whatsapp ?? false;
  const contactSharingEmail = currentValues.contactSharing?.email ?? false;

  const [isFlashing, setIsFlashing] = useState(false);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => (): void => {
      if (flashTimeoutRef.current !== null) {
        clearTimeout(flashTimeoutRef.current);
      }
    },
    [],
  );

  const handleGuard = useCallback(
    (field: ContactSharingFieldType, newValue: boolean): boolean => {
      if (newValue) {
        return true;
      }
      const otherField =
        field === "whatsapp" ? contactSharingEmail : contactSharingWhatsApp;
      if (otherField === true) {
        return true;
      }
      setIsFlashing(true);
      if (flashTimeoutRef.current !== null) {
        clearTimeout(flashTimeoutRef.current);
      }
      flashTimeoutRef.current = setTimeout((): void => {
        setIsFlashing(false);
      }, 3000);
      return false;
    },
    [contactSharingWhatsApp, contactSharingEmail],
  );

  const isNone = !contactSharingWhatsApp && !contactSharingEmail;

  return {
    handleGuard,
    isFlashing,
    showHint: isNone || isFlashing,
  };
}

function booleanToSelectValue(value: boolean): string {
  return value ? "real-time" : "off";
}

function selectValueToBoolean(value: string): boolean {
  return value === "real-time";
}

interface ChannelSelectRowProps {
  readonly label: string;
  readonly whatsappField: PreferencesFieldPathType;
  readonly emailField: PreferencesFieldPathType;
  readonly control: PreferencesFormControlType;
}

function ChannelSelectRow({
  label,
  whatsappField,
  emailField,
  control,
}: ChannelSelectRowProps): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 rounded-md border border-foreground/15 bg-card p-4">
      <p className="font-serif text-base font-normal text-foreground">
        {label}
      </p>
      <div className="flex gap-6">
        <div className="flex flex-1 flex-col gap-1">
          <Label
            htmlFor={whatsappField}
            className="text-pds-micro font-medium uppercase leading-none tracking-[0.08em] text-muted-foreground"
          >
            {i18n.t("user.profile.preferences.whatsapp")}
          </Label>
          <Controller
            control={control}
            name={whatsappField}
            render={({ field }): React.JSX.Element => (
              <Select
                value={booleanToSelectValue(field.value === true)}
                onValueChange={(v): void => {
                  field.onChange(selectValueToBoolean(v));
                }}
              >
                <SelectTrigger
                  id={whatsappField}
                  className="w-full bg-background"
                  size="sm"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="real-time">
                    {i18n.t("user.profile.preferences.realTime")}
                  </SelectItem>
                  <SelectItem value="off">
                    {i18n.t("user.profile.preferences.off")}
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <Label
            htmlFor={emailField}
            className="text-pds-micro font-medium uppercase leading-none tracking-[0.08em] text-muted-foreground"
          >
            {i18n.t("user.profile.preferences.email")}
          </Label>
          <Controller
            control={control}
            name={emailField}
            render={({ field }): React.JSX.Element => (
              <Select
                value={booleanToSelectValue(field.value === true)}
                onValueChange={(v): void => {
                  field.onChange(selectValueToBoolean(v));
                }}
              >
                <SelectTrigger
                  id={emailField}
                  className="w-full bg-background"
                  size="sm"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="real-time">
                    {i18n.t("user.profile.preferences.realTime")}
                  </SelectItem>
                  <SelectItem value="off">
                    {i18n.t("user.profile.preferences.off")}
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>
      </div>
    </div>
  );
}

interface PreferenceSwitchCardProps {
  readonly label: string;
  readonly field: PreferencesFieldPathType;
  readonly control: PreferencesFormControlType;
  readonly onGuardedChange?: (checked: boolean) => boolean;
}

function PreferenceSwitchCard({
  label,
  field,
  control,
  onGuardedChange,
}: PreferenceSwitchCardProps): React.JSX.Element {
  return (
    <Controller
      control={control}
      name={field}
      render={({ field: formField }): React.JSX.Element => (
        <Label className="flex flex-1 cursor-pointer items-center gap-2 rounded-md border border-foreground/15 bg-card p-3">
          <Switch
            checked={formField.value === true}
            onCheckedChange={(checked): void => {
              if (onGuardedChange !== undefined && !onGuardedChange(checked)) {
                return;
              }
              formField.onChange(checked);
            }}
          />
          <span className="text-sm font-medium leading-none">{label}</span>
        </Label>
      )}
    />
  );
}

export function PreferencesForm({
  initialData,
  onFieldChange,
  isLoading,
  beforeActions,
}: PreferencesFormProps): React.JSX.Element {
  if (isLoading || initialData === undefined) {
    return (
      <div className="flex flex-col gap-4 px-4 py-6">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <PreferencesFormContent
      initialData={initialData}
      onFieldChange={onFieldChange}
      beforeActions={beforeActions}
    />
  );
}

interface PreferencesFormContentProps {
  readonly initialData: ParticipantPreferences;
  readonly onFieldChange: (data: ParticipantPreferences) => void;
  readonly beforeActions?: React.ReactNode;
}

function PreferencesFormContent({
  initialData,
  onFieldChange,
  beforeActions,
}: PreferencesFormContentProps): React.JSX.Element {
  const { control } = useForm<ParticipantPreferences>({
    resolver: zodResolver(ParticipantPreferencesSchema),
    defaultValues: initialData,
  });

  useForwardFieldChanges(control, onFieldChange);
  const { handleGuard, showHint } = useContactSharingGuard(control);

  return (
    <form
      onSubmit={(e): void => {
        e.preventDefault();
      }}
      className="flex flex-col gap-8 px-4 py-6"
      data-testid="preferences-form"
    >
      {/* Notifications */}
      <div className="flex flex-col gap-2">
        <p className="text-pds-micro font-medium uppercase leading-none tracking-[0.08em] text-muted-foreground">
          {i18n.t("user.profile.preferences.notifications")}
        </p>

        {/* Incoming Requests */}
        <ChannelSelectRow
          label={i18n.t("user.profile.preferences.incomingRequests")}
          whatsappField="incomingRequestNotifications.whatsapp"
          emailField="incomingRequestNotifications.email"
          control={control}
        />

        {/* Accepted Requests + Contact Sharing */}
        <div className="flex flex-col gap-4 rounded-md border border-foreground/15 bg-card p-4">
          <p className="font-serif text-base font-normal text-foreground">
            {i18n.t("user.profile.preferences.acceptedRequests")}
          </p>
          <div className="flex gap-6">
            <div className="flex flex-1 flex-col gap-1">
              <Label
                htmlFor="acceptedRequestNotifications.whatsapp"
                className="text-pds-micro font-medium uppercase leading-none tracking-[0.08em] text-muted-foreground"
              >
                {i18n.t("user.profile.preferences.whatsapp")}
              </Label>
              <Controller
                control={control}
                name="acceptedRequestNotifications.whatsapp"
                render={({ field }): React.JSX.Element => (
                  <Select
                    value={booleanToSelectValue(field.value)}
                    onValueChange={(v): void => {
                      field.onChange(selectValueToBoolean(v));
                    }}
                  >
                    <SelectTrigger
                      id="acceptedRequestNotifications.whatsapp"
                      className="w-full bg-background"
                      size="sm"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="real-time">
                        {i18n.t("user.profile.preferences.realTime")}
                      </SelectItem>
                      <SelectItem value="off">
                        {i18n.t("user.profile.preferences.off")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <Label
                htmlFor="acceptedRequestNotifications.email"
                className="text-pds-micro font-medium uppercase leading-none tracking-[0.08em] text-muted-foreground"
              >
                {i18n.t("user.profile.preferences.email")}
              </Label>
              <Controller
                control={control}
                name="acceptedRequestNotifications.email"
                render={({ field }): React.JSX.Element => (
                  <Select
                    value={booleanToSelectValue(field.value)}
                    onValueChange={(v): void => {
                      field.onChange(selectValueToBoolean(v));
                    }}
                  >
                    <SelectTrigger
                      id="acceptedRequestNotifications.email"
                      className="w-full bg-background"
                      size="sm"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="real-time">
                        {i18n.t("user.profile.preferences.realTime")}
                      </SelectItem>
                      <SelectItem value="off">
                        {i18n.t("user.profile.preferences.off")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          {/* Contact Sharing (inside Accepted Requests box per Figma) */}
          <div className="flex flex-col gap-2">
            <p className="text-pds-micro font-medium uppercase leading-none tracking-[0.08em] text-muted-foreground">
              {i18n.t("user.profile.preferences.contactSharing")}
            </p>
            <p className="text-sm text-muted-foreground">
              {i18n.t("user.profile.preferences.contactSharingDescription")}
            </p>
            <div className="flex gap-4">
              <div data-testid="contact-sharing-whatsapp-toggle">
                <PreferenceSwitchCard
                  label={i18n.t("user.profile.preferences.whatsapp")}
                  field="contactSharing.whatsapp"
                  control={control}
                  onGuardedChange={(checked): boolean =>
                    handleGuard("whatsapp", checked)
                  }
                />
              </div>
              <div data-testid="contact-sharing-email-toggle">
                <PreferenceSwitchCard
                  label={i18n.t("user.profile.preferences.email")}
                  field="contactSharing.email"
                  control={control}
                  onGuardedChange={(checked): boolean =>
                    handleGuard("email", checked)
                  }
                />
              </div>
            </div>
            {/* Only rendered when it applies. Kept permanently in the layout
              as an invisible line it reserved 40px under the toggles, which
              read as a gap between this card and the next one. Nothing above
              it moves when it appears, so the toggle just tapped stays put. */}
            {showHint ? (
              <p
                data-testid="contact-sharing-guard-hint"
                className="text-sm font-medium text-destructive"
              >
                {i18n.t("user.profile.preferences.contactSharingError")}
              </p>
            ) : null}
          </div>
        </div>

        {/* Event Notifications */}
        <div className="flex flex-col gap-4 rounded-md border border-foreground/15 bg-card p-4">
          <div className="flex flex-col gap-2">
            <p className="font-serif text-base font-normal text-foreground">
              {i18n.t("user.profile.preferences.eventNotifications")}
            </p>
            <p className="text-sm text-muted-foreground">
              {i18n.t("user.profile.preferences.eventNotificationsDescription")}
            </p>
          </div>
          <div
            className="flex gap-4"
            data-testid="event-notifications-whatsapp-toggle"
          >
            <PreferenceSwitchCard
              label={i18n.t("user.profile.preferences.whatsapp")}
              field="eventNotifications.whatsapp"
              control={control}
            />
          </div>
        </div>
      </div>

      {beforeActions}
    </form>
  );
}
